import { UseMutationResult, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import { EditorSection, SubmitPanel, Title } from "../reuseables/editor.comp";
import { useEffect, useState } from "react";
import { FancyTextArea, LabelAndInput } from "../reuseables/form.comp";
import pickupAddrCore from "../../core/pickupAddr.core";
import { PickupAddr } from "../../types/pickupAddr";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import shipCore from "../../core/ship.core";
import ShipInfoCreatorBasic from "./shipInfoCreatorBasic.comp";
import ShipInfoCreatorPickup from "./shipInfoCreatorPickup.comp";
import ShipInfoCreatorSf from "./shipInfoCreatorSf.comp";
import useShop from "../../hooks/useShop.hook";

import { inputCssNoMy } from "../reuseables/native.comp";
import { toast } from "react-toastify";
import ManualOrderCustomer from "./manualOrderCustomer.comp";
import ManualOrderProducts from "./manualOrderProducts.comp";
import ManuaOrderPaymentsComp from "./manualOrderPayments.comp";
import { ManualOrderFormData } from "../../types/order";

interface Product {
  productId: string;
  name: string;
  quantity: number;
  price: number;
  inventory: number;
  discount: number | null;
  productType?: string;
}

export const PaymentPortionArr = ["fullPayment", "partPayment", "deposit", "balance"];

export default function ManualOrderEditor({
  methods,
  mutation,
  btnName,
}: {
  methods: UseFormReturn<ManualOrderFormData, any>;
  mutation: UseMutationResult<void, Error, ManualOrderFormData, unknown>;
  btnName: string;
}) {
  const { t, msg } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const router = useRouter();
  const { selectedShop } = useShop();
  const [loading, setIsloading] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const { data: pickupAddrs } = useQuery({
    queryKey: ["pickupAddrs"],
    queryFn: () => pickupAddrCore.list(),
  });

  async function onSubmit(data: any) {
    setIsloading(true);
    if (data.shipType === "pickup") {
      const pickupAddrId = data.pickupAddrId;
      if (!pickupAddrId) {
        toast.error("Please select a pickup address");
        return;
      }
    }

    const stucturedformData = {
      ...data,
      products: selectedProducts.map((product) => ({
        productId: product.productId,
        quantity: product.quantity,
        discount: product.discount || 0,
      })),
    };

    mutation.mutate(stucturedformData);
    setIsloading(false);
  }

  // const productInv = async (productId: string) => {
  //   return await inventoryCore.loadSingleInventoryCount({ id: productId });
  // };

  if (!isMounted) {
    return null;
  }

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="pb-20">
      <ManualOrderProducts methods={methods} selectedProducts={selectedProducts} setSelectedProducts={setSelectedProducts} />
      <ManualOrderCustomer methods={methods} />

      <EditorSection>
        <Title>{t(msg.order.status)}</Title>
        <OrderStatusSelectSection methods={methods} />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.ship.shipping)}</Title>
        <div className="space-y-6">
          <ShipTypeSelector methods={methods} />
          <Form methodsShipType={methods} pickupAddrs={pickupAddrs || []} />
        </div>
      </EditorSection>
      {/* <EditorSection>
        <Title>{t(msg.order.paymentType)}</Title>
        <PayType methods={methods} />
      </EditorSection> */}
      <ManuaOrderPaymentsComp methods={methods} />
      {/* <EditorSection>
        <Title>{t(msg.payment.payments)}</Title>
        <Payments methods={methods} />
      </EditorSection> */}
      <EditorSection>
        <Title>{t(msg.order.note)}</Title>
        <FancyTextArea
          className="note-text"
          register={() => methods.register("orderNote")}
          placeholder={t(msg.order.note)}
          rows={3}
        />
      </EditorSection>
      <EditorSection>
        <Title>{t(msg.order.comment)}</Title>
        <FancyTextArea
          className="comment-test"
          register={() => methods.register("comments")}
          placeholder={t(msg.order.comment)}
          rows={5}
        />
      </EditorSection>
      <input {...methods.register("currency")} hidden value={selectedShop?.defaultCurrency} name="currency" />
      <SubmitPanel>
        <button type="button" onClick={() => router.push("/orders")}>
          {t(msg.form.cancel)}
        </button>
        {loading ? (
          <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-her-robinEggBlue mx-2"></span>
        ) : (
          <input type="submit" value={btnName} />
        )}
      </SubmitPanel>
    </form>
  );
}

export const OrderStatusSelectSection = ({ methods }: { methods: UseFormReturn<ManualOrderFormData, any> }) => {
  const { t, msg } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <div id="order-status-selector" className="grid grid-cols-2 gap-4">
      <div id="order-status-container">
        <label className="block font-medium mb-2">{t(msg.order.orderStatus)}</label>
        <select
          id="order-status"
          {...methods.register("orderStatus")}
          className={`w-full ${inputCssNoMy}`}
          defaultValue="processing"
        >
          <option value="pending">{t(msg.order.pending)}</option>
          <option value="processing">{t(msg.order.processing)}</option>
          <option value="shipped">{t(msg.order.shipped)}</option>
          <option value="delivered">{t(msg.order.delivered)}</option>
          <option value="cancelled">{t(msg.order.cancelled)}</option>
          <option value="returned">{t(msg.order.returned)}</option>
        </select>
      </div>

      <div id="payment-status-container">
        <label className="block  font-medium mb-2">{t(msg.order.paymentStatus)}</label>
        <select
          id="payment-status"
          {...methods.register("paymentStatus")}
          className={`w-full ${inputCssNoMy}`}
          defaultValue="awaiting"
        >
          <option value="awaiting">{t(msg.payment.awaiting)}</option>
          <option value="deposit">{t(msg.payment.paid)}</option>
          <option value="paid">{t(msg.payment.refunded)}</option>
        </select>
      </div>
    </div>
  );
};

const ShipTypeSelector = ({ methods }: { methods: UseFormReturn<ManualOrderFormData, any> }) => {
  const { t, msg } = useTranslation();
  const { locale, isReady } = useRouter();
  const [selectedShipOption, setSelectedShipOption] = useState<{ id: string; name: string }>({ id: "", name: "" });

  const {
    data: shipSettings,
    isPending,
    isError,
    failureReason,
  } = useQuery({
    queryKey: ["shipSettings", locale],
    queryFn: shipCore.getShipSettings,
    enabled: isReady,
  });

  const selectedShipType = methods.watch("shipType");

  // const availableNames =
  //   shipSettings
  //     ?.filter((setting) => setting.options.some((option) => option.shipType === selectedShipType))
  //     .flatMap((setting) =>
  //       setting.options.filter((option) => option.shipType === selectedShipType).map((option) => option.name)
  //     ) || [];

  const availableOptions: { id: string; name: string }[] =
    shipSettings
      ?.filter((setting) => setting.options.some((option) => option.shipType === selectedShipType))
      .flatMap((setting) =>
        setting.options
          .filter((option) => option.shipType === selectedShipType)
          .map((option) => ({
            id: option.id,
            name: option.name,
          }))
      ) || [];

  const availableNames = availableOptions.map((option) => option.name);

  useEffect(() => {
    if (!availableNames.includes(selectedShipOption.name)) {
      setSelectedShipOption({ id: "", name: "" }); // Reset if it's not in the new list
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShipType, shipSettings]);

  const selectedOption = shipSettings
    ?.flatMap((setting) => setting.options)
    .find((option) => option.name === selectedShipOption.name);

  const totalShippingFee = selectedOption
    ? selectedOption.feeOptions.reduce((sum, fee) => sum + ("flat" in fee.setting ? fee.setting.flat ?? 0 : 0), 0)
    : 0;

  useEffect(() => {
    methods.setValue("shipping", totalShippingFee);
  }, [totalShippingFee, methods]);

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!shipSettings) return null;

  return (
    <>
      <select id="shiptype" {...methods.register("shipType")} className={`w-full ${inputCssNoMy}`}>
        {["pickup", "sf", "basic"].map(
          (type) =>
            shipSettings?.some((setting) => setting.options.some((option) => option.shipType === type)) && (
              <option key={type} value={type}>
                {t(msg.ship[`shipType${type.charAt(0).toUpperCase() + type.slice(1)}`])}
              </option>
            )
        )}
      </select>

      {availableOptions.length > 0 && (
        <select
          id="shipt-type-name"
          className={`mb-2 w-full ${inputCssNoMy}`}
          value={selectedShipOption.id}
          required
          {...methods.register("shipOptionId")}
          onChange={(e) => {
            const selectedId = e.target.value;
            const selectedOption = availableOptions.find((opt) => opt.id === selectedId);
            if (selectedOption) {
              setSelectedShipOption(selectedOption);
            } else {
              setSelectedShipOption({ id: "", name: "" });
            }
          }}
        >
          <option value="">{"Select Prefer Option"}</option>
          {availableOptions.map((name) => (
            <option key={name.id} value={name.id}>
              {name.name}
            </option>
          ))}
        </select>
      )}

      <LabelAndInput
        type="number"
        register={methods.register}
        fieldName="shipping"
        topLabelCss="w-full"
        labelCss="mt-2"
        registerOptions={{ min: 0, valueAsNumber: true }}
        label={t(msg.order.shipFee)}
        errors={methods.formState.errors}
      />
    </>
  );
};

const Form = ({
  methodsShipType,
  pickupAddrs,
}: {
  methodsShipType: UseFormReturn<ManualOrderFormData, any>;
  pickupAddrs: PickupAddr[];
}) => {
  const selectedShipType = useWatch({
    name: "shipType",
    control: methodsShipType.control,
  });

  switch (selectedShipType) {
    case "basic":
      return <ShipInfoCreatorBasic methods={methodsShipType} />;
    case "pickup":
      return <ShipInfoCreatorPickup methods={methodsShipType} pickupAddrs={pickupAddrs} />;
    case "sf":
      return <ShipInfoCreatorSf methods={methodsShipType} />;
    default:
      return null;
  }
};
